import * as React from 'react';
import {
  Box,
  StackLayout,
  StackItem,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { PageContainer } from '@leagueplatform/web-common-components';
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
  useSectionNodeStates,
} from '@leagueplatform/masonry-engine';

export const NODE_TYPE_CHAT_HISTORY_PAGE_LAYOUT = 'chatHistoryPageLayout';

const CHAT_LIST_WIDTH = '340px';

type ChatListNodeState = {
  hasActiveChat?: boolean;
};

type ChatHistoryPageLayoutSections = {
  header: MasonryEngineNode[];
  chatList: MasonryEngineNode[];
  chat: MasonryEngineNode[];
};

type ChatHistoryPageLayoutNode = MasonryEngineNode<
  typeof NODE_TYPE_CHAT_HISTORY_PAGE_LAYOUT,
  {}, // no properties
  {}, // onLoad handled automatically by masonry engine
  ChatHistoryPageLayoutSections
>;

export type ChatHistoryPageLayoutRendererProps =
  MasonryEngineNodeRendererProps<ChatHistoryPageLayoutNode>;

/**
 * Responsive page-level layout for the chat history feature.
 *
 * Renders three masonry sections — `header`, `chatList`, and `chat` — in a
 * sidebar + main content arrangement on desktop. On mobile, only one view is
 * shown at a time: the chat list or the active chat conversation.
 *
 * Active-chat detection is driven by masonry node state: the layout reads
 * `hasActiveChat` from the `chatList` section nodes via `useSectionNodeStates`.
 */
export const ChatHistoryPageLayoutRenderer = ({
  header,
  chatList,
  chat,
  nodeId,
  nodeData,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: ChatHistoryPageLayoutRendererProps) => {
  const isTabletMobile = useMediaQuery(queryHelpers.down('laptop'));

  const chatListNodeStates = useSectionNodeStates(
    nodeData.sections?.chatList ?? [],
  );
  const isChatActive = chatListNodeStates.some(
    (state) => (state as ChatListNodeState)?.hasActiveChat,
  );

  const showHeader = !(isTabletMobile && isChatActive);
  const showChatList = !(isTabletMobile && isChatActive);
  const showChat = !isTabletMobile || isChatActive;

  const testId = locatorId ?? nodeId;
  const combinedClassName = [engineClassName, className]
    .filter(Boolean)
    .join(' ');

  const hasMainContent = chatList || chat;

  return (
    <StackLayout
      ref={rootElementRef}
      orientation="vertical"
      spacing={{
        '@initial': '$two',
        '@tablet': '$none',
        '@mobileLandscape': '$none',
        '@mobile': '$none',
      }}
      horizontalAlignment="stretch"
      className={combinedClassName || undefined}
      data-testid={testId}
      css={{ height: '100%', width: '100%', ...css }}
    >
      {/* Header — full width, outside PageContainer */}
      {header && (
        <Box
          data-testid={`${testId}-header`}
          style={{ display: showHeader ? 'block' : 'none' }}
        >
          {header}
        </Box>
      )}
      {/* Main content — wrapped in PageContainer */}
      {hasMainContent && (
        <PageContainer
          css={{
            width: '100%',
            transition: 'padding 200ms ease-in',
            '@tablet': { paddingInline: '$none' },
            '@mobileLandscape': { paddingInline: '$none' },
            '@mobile': { paddingInline: '$none' },
          }}
        >
          <StackLayout
            orientation="horizontal"
            spacing="$two"
            css={{ flex: 1 }}
          >
            {/* Chat list sidebar */}
            {chatList && (
              <StackItem
                baseWidth={isTabletMobile ? '100%' : CHAT_LIST_WIDTH}
                growFactor={1}
                shrinkFactor={0}
                data-testid={`${testId}-chat-list`}
                style={{ display: showChatList ? 'flex' : 'none' }}
              >
                {chatList}
              </StackItem>
            )}
            {/* Chat content area */}
            {chat && (
              <StackItem
                growFactor={9999}
                data-testid={`${testId}-chat`}
                style={{ display: showChat ? 'flex' : 'none' }}
                css={{ minWidth: 0 }}
              >
                {chat}
              </StackItem>
            )}
          </StackLayout>
        </PageContainer>
      )}
    </StackLayout>
  );
};
